import {
  selectPlan,
  selectPlanItems,
  selectPlanItemsEditing,
  setPlanItemsEditingAction,
  setPlanViewAction,
} from "@store/plan/planReducer";
import { useAppDispatch, useAppSelector } from "@store/store";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { Text } from "@ui-kit/Text/Text";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { useTranslation } from "react-i18next";
import { Button } from "@ui-kit/Button";
import { RemoveIcon } from "@icons/RemoveIcon";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { TextInput } from "@ui-kit/TextInput/TextInput";
import { Form } from "@ui-kit/Form/Form";
import { logError } from "@lib/logger";
import {
  useRemovePlanItemsMutation,
  useUpdatePlanMutation,
} from "@store/services/plan";
import { PlanItem } from "@dto/PlanItem";
import { LinkButton } from "@ui-kit/LinkButton/LinkButton";
import { SortableItem } from "./SortableItem";
import {
  calculateListItems,
  Day,
  DraggableFlatListItem,
} from "@modules/entities/PlanItemList/helpers";
import { ConfirmationModal } from "@modules/components/ConfirmationModal/ConfirmationModal";
import { useNavigate } from "@hooks/useNavigate/useNavigate";

import "./PlanEdit.css";

interface PlanEditForm {
  name: string;
}

const DayItem: FunctionComponent<{
  item: DraggableFlatListItem;
  onClearDayPress: (dayIndex: number | null) => () => void;
}> = ({ item, onClearDayPress }) => {
  const { t } = useTranslation();
  const planItemsEditing = useAppSelector(selectPlanItemsEditing);
  const hasPlanItemsForDay = planItemsEditing.some(
    (x) => x.dayIndex === item.dayIndex && x.type === "planItem",
  );

  return (
    <div className="day-container">
      <Text variant="fontHeadingMedium">{(item.item as Day).title}</Text>
      {item.dayIndex !== null && hasPlanItemsForDay && (
        <LinkButton type="destructive" onPress={onClearDayPress(item.dayIndex)}>
          {t("screens.plan.clearDay")}
        </LinkButton>
      )}
    </div>
  );
};

/**
 * Assign the appropriate day and position to items, should include full list of days & plan items
 */
const assignDayIndexPositionOnMove = (items: DraggableFlatListItem[]) => {
  let currentDayIndex = 0 as number | null;
  let currentPosition = 0;

  const updatedItems = items.map((item) => {
    if (item.type === "day") {
      currentDayIndex = item.dayIndex;
      return item;
    }

    return {
      ...item,
      item: {
        ...item.item,
        dayIndex: currentDayIndex,
        position: ++currentPosition,
      },
      dayIndex: currentDayIndex,
    };
  });

  return updatedItems;
};

const PlanEdit: FunctionComponent = () => {
  const planItems = useAppSelector(selectPlanItems);
  const dispatch = useAppDispatch();
  const plan = useAppSelector(selectPlan)!;
  const planItemsEditing = useAppSelector(selectPlanItemsEditing);
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const hasPlanDates = (plan?.dayDuration ?? 0) > 0;
  // First day header is not sortable, to prevent dragging above it.
  const sortableItems = hasPlanDates
    ? planItemsEditing.slice(1)
    : planItemsEditing;
  const firstDayItem = hasPlanDates ? planItemsEditing[0] : null;
  const [isDeleteAllStopsConfirmOpen, setIsDeleteAllStopsConfirmOpen] =
    useState(false);
  const [isClearDayConfirmIndex, setIsClearDayConfirmIndex] = useState<
    number | null
  >(null);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      setPlanItemsEditingAction(calculateListItems(plan, planItems, "all", [])),
    );
  }, [dispatch, plan, planItems]);

  interface UpdatePlanItemRequest {
    id: number;
    position?: number;
    dayIndex?: number;
    _destroy?: boolean;
  }

  const [form] = Form.useForm();
  const [updatePlan, { isLoading: isUpdatingPlan }] = useUpdatePlanMutation();
  const [removePlanItems, { isLoading: isRemovingPlanItems }] =
    useRemovePlanItemsMutation();
  const isLoading = isUpdatingPlan || isRemovingPlanItems;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = sortableItems.findIndex(
          (x) => x.item.id === active.id,
        );
        const newIndex = sortableItems.findIndex((x) => x.item.id === over?.id);
        const planItemsReordered = arrayMove(sortableItems, oldIndex, newIndex);
        const updatedPlanItems = assignDayIndexPositionOnMove(
          firstDayItem
            ? [firstDayItem, ...planItemsReordered]
            : planItemsReordered,
        );
        dispatch(setPlanItemsEditingAction(updatedPlanItems));
      }
    },
    [dispatch, firstDayItem, sortableItems],
  );

  const handleRemovePress = useCallback(
    (id: number) => () => {
      const planItemsReordered = planItemsEditing.filter(
        (x) => x.item.id !== id,
      );
      dispatch(setPlanItemsEditingAction(planItemsReordered));
    },
    [dispatch, planItemsEditing],
  );

  const handleSubmit = useCallback(
    async (values: PlanEditForm) => {
      try {
        const updateRequest = planItemsEditing
          .map((item) => {
            if (item.type !== "planItem") {
              return;
            }

            const planItem = item.item as PlanItem;

            return {
              id: planItem.id,
              position: planItem.position,
              dayIndex: planItem.dayIndex,
            } as UpdatePlanItemRequest;
          })
          .filter((x) => x !== undefined);

        const removedItemIds = planItems
          .filter((item) => {
            return !planItemsEditing.some(
              (editingItem) => editingItem.item.id === item.id,
            );
          })
          .map((item) => item.id);

        if (removedItemIds.length > 0) {
          // TODO: fix backend to allow deletions with update (_destroy does not work currently)
          await removePlanItems({
            planItemIds: removedItemIds,
            planId: plan.id,
          });
        }

        if (updateRequest.length > 0) {
          await updatePlan({
            id: plan.id,
            name: values.name,
            planItemsAttributes: updateRequest,
          });
        }
        navigate(-1);
      } catch (error) {
        logError(error);
      }
    },
    [
      navigate,
      plan?.id,
      planItems,
      planItemsEditing,
      removePlanItems,
      updatePlan,
    ],
  );

  const handleCancelPress = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleDeleteAllStopsPress = useCallback(() => {
    setIsDeleteAllStopsConfirmOpen(true);
  }, []);

  const handleDeleteAllStopsConfirmClose = useCallback(() => {
    setIsDeleteAllStopsConfirmOpen(false);
  }, []);

  const handleClearDayConfirmClose = useCallback(() => {
    setIsClearDayConfirmIndex(null);
  }, []);

  const handleClearDayConfirm = useCallback(() => {
    const newlyUnscheduledItems = planItemsEditing.filter(
      (item) =>
        item.dayIndex === isClearDayConfirmIndex && item.type === "planItem",
    );

    const planItemsReordered = planItemsEditing.filter((item) => {
      return (
        item.dayIndex !== isClearDayConfirmIndex || item.type !== "planItem"
      );
    });

    const updatedPlanItems = assignDayIndexPositionOnMove([
      ...planItemsReordered,
      ...newlyUnscheduledItems,
    ]);
    dispatch(setPlanItemsEditingAction(updatedPlanItems));
    setIsClearDayConfirmIndex(null);
  }, [dispatch, isClearDayConfirmIndex, planItemsEditing]);

  const handleDeleteAllStopsConfirm = useCallback(async () => {
    const planItemsReordered = planItemsEditing.filter((item) => {
      return item.type !== "planItem";
    });
    await removePlanItems({
      planItemIds: planItemsEditing
        .filter((item) => item.type === "planItem")
        .map((item) => item.item.id as number),
      planId: plan.id,
    });
    dispatch(setPlanItemsEditingAction(planItemsReordered));
    setIsDeleteAllStopsConfirmOpen(false);
    dispatch(setPlanViewAction("list"));
  }, [dispatch, plan, planItemsEditing, removePlanItems]);

  const handleClearDayPress = useCallback(
    (dayIndex: number | null) => () => {
      setIsClearDayConfirmIndex(dayIndex);
    },
    [],
  );

  return (
    <>
      <div className="plan-edit-component">
        <div className="plan-name-container">
          <Text variant="fontHeadingXsmall" color={colors.neutral700}>
            {t("screens.planEdit.planTitle")}
          </Text>
          <Form
            form={form}
            name="plan"
            layout="vertical"
            onFinish={handleSubmit}
            disabled={isLoading}
            initialValues={{
              name: plan?.name ?? t("screens.plan.defaultPlanTitle"),
            }}>
            <TextInput
              name="name"
              type="text"
              placeholder={t("screens.planEdit.planTitlePlaceholder")}
            />
          </Form>
        </div>
        <div className="plan-items-container">
          {firstDayItem && (
            <DayItem
              item={firstDayItem}
              onClearDayPress={handleClearDayPress}
            />
          )}
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}>
            <SortableContext
              disabled={isLoading}
              items={sortableItems.map((item) => item.item)}
              strategy={verticalListSortingStrategy}>
              {sortableItems.map((item) => (
                <SortableItem
                  key={item.item.id}
                  id={item.item.id}
                  isSortEnabled={item.type === "planItem"}
                  className={
                    item.type === "planItem"
                      ? "plan-item-container"
                      : "day-item-container"
                  }>
                  <>
                    {item.type === "planItem" && (
                      <>
                        <span
                          className="plan-item-remove-icon"
                          onClick={handleRemovePress(item.item.id as number)}>
                          <RemoveIcon />
                        </span>
                        <Text
                          variant="fontHeadingXsmall"
                          color={colors.neutral700}>
                          {(item.item as PlanItem).plannable.name}
                        </Text>
                      </>
                    )}
                    {item.type === "day" && (
                      <DayItem
                        item={item}
                        onClearDayPress={handleClearDayPress}
                      />
                    )}
                  </>
                </SortableItem>
              ))}
            </SortableContext>
          </DndContext>
        </div>
        <div className="actions-container">
          <div className="actions-left">
            <LinkButton
              type="destructive"
              onPress={handleDeleteAllStopsPress}
              disabled={isLoading}>
              {t("screens.planEdit.deleteAllStops")}
            </LinkButton>
          </div>
          <div className="actions-right">
            <Button
              type="outlinePrimary"
              onPress={handleCancelPress}
              disabled={isLoading}>
              {t("common.cancel")}
            </Button>
            <Button type="primary" onPress={form.submit} disabled={isLoading}>
              {t("common.save")}
            </Button>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={isDeleteAllStopsConfirmOpen}
        onClose={handleDeleteAllStopsConfirmClose}
        onConfirm={handleDeleteAllStopsConfirm}
        title={t("screens.plan.deleteAllStopsConfirmationTitle")}
        message={t("screens.plan.deleteAllStopsConfirmationMessage")}
      />
      <ConfirmationModal
        isOpen={isClearDayConfirmIndex !== null}
        onClose={handleClearDayConfirmClose}
        onConfirm={handleClearDayConfirm}
        title={t("screens.plan.clearDayConfirmationTitle")}
        message={t("screens.plan.clearDayConfirmationMessage")}
        confirmLabel={t("screens.plan.clearDayConfirmationButton")}
      />
    </>
  );
};

export { PlanEdit };
